import { useState } from 'react';
import { InputProps, ModalConfirmProps, ModalShowDataProps } from '../../../interfaces/common';
import { Form } from 'antd';
import { useGlobalStore } from '../../../stores/globalState';
import AuthService from '../services/authServices';
import { HTTP_ERROR_MESSAGE, UNKNOWN_ERROR } from '../../../utils/constants/strings';
import { NOTIFY_TYPE } from '../../../utils/constants/enums';
import { CHANGE_PASSWORD } from '../../../utils/constants/itemScreen';
import { RULE } from '../../../utils/constants/regex';
import { OpenToast, renderMessageID } from '../../../utils/helpers/function';

export const useChangePassword = () => {
  const [form] = Form.useForm();

  const [modalChangePassword, setModalChangePassword] = useState<ModalShowDataProps>({
    isOpen: false,
    title: '',
    content: '',
  } as ModalShowDataProps);

  const [modalConfirm, setModalConfirm] = useState<ModalConfirmProps>({
    isOpen: false,
    title: '',
    modalType: 'success',
  } as ModalConfirmProps);

  const { setIsLoading } = useGlobalStore();

  const handleChangePassword = async () => {
    try {
      setIsLoading(true);
      const response = await AuthService.changePassword(form.getFieldsValue());
      if (response && response.isError) {
        const errorMessage = response?.message || HTTP_ERROR_MESSAGE[500];
        OpenToast({ message: errorMessage }, NOTIFY_TYPE.ERROR);
        return;
      }

      form.resetFields();
      setModalChangePassword({ isOpen: false, content: '' });
      setModalConfirm({ isOpen: true, title: response.data.message, modalType: 'success' });
    } catch (e: unknown) {
      if (e instanceof Error) {
        OpenToast({ message: e.message }, NOTIFY_TYPE.ERROR);
      } else {
        OpenToast({ message: UNKNOWN_ERROR }, NOTIFY_TYPE.ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelForm = () => {
    form.resetFields();
    setModalChangePassword({ isOpen: false, content: '' });
  };

  const fieldsChangePassword: InputProps[] = [
    {
      label: CHANGE_PASSWORD.PASSWORD_CURRENT,
      name: 'password_current',
      maxLength: 16,
      validate: [
        {
          required: true,
          message: renderMessageID('MSE00001', [CHANGE_PASSWORD.PASSWORD_CURRENT]),
        },
      ],
    },
    {
      label: CHANGE_PASSWORD.PASSWORD_NEW,
      name: 'password_new',
      maxLength: 16,
      validate: [
        {
          required: true,
          message: renderMessageID('MSE00001', [CHANGE_PASSWORD.PASSWORD_NEW]),
        },
        {
          pattern: new RegExp(RULE.PASSWORD),
          message: renderMessageID('MSE00005'),
        },
      ],
    },
    {
      label: CHANGE_PASSWORD.PASSWORD_CONFIRM,
      name: 'password_confirm',
      maxLength: 16,
      dependencies: ['password_new'],
      validate: [
        {
          required: true,
          message: `${renderMessageID('MSE00001', [CHANGE_PASSWORD.PASSWORD_CONFIRM])}`,
        },
        /* eslint-disable */
        ({ getFieldValue }: { getFieldValue: (name: string) => any }) => ({
          validator(_: any, value: any): Promise<void> {
            if (!value || getFieldValue('password_new') === value) {
              return Promise.resolve();
            }
            return Promise.reject(
              new Error(`${renderMessageID('MSE00006', [CHANGE_PASSWORD.PASSWORD_NEW, CHANGE_PASSWORD.PASSWORD_CONFIRM])}`)
            );
          },
        }),
      ],
    },
  ];

  return {
    form,
    fieldsChangePassword,
    modalChangePassword,
    modalConfirm,
    setModalChangePassword,
    handleCancelForm,
    handleChangePassword,
  };
};
